import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import { Modal, Platform, Pressable, View } from 'react-native';
import { CoreConfig } from '../../../CoreConfig';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { component } from '../../Decorators/Component';
import { DatePickerSelectorView } from './DatePickerSelectorView';
import { DatePickerCalendarView } from './DatePickerCalendarView';
import { AnimatedTextView } from '../Typography/AnimatedTextView';
import { AnimatedPressable } from '../Buttons/AnimatedPressable';
import { CardView } from '../Cards/CardView';
import { useAnimation } from '../Animations/AnimationHooks';
const CenterPosition = Platform.select({
    default: 16,
    android: 16,
    ios: 16
});
const TopPosition = Platform.select({
    default: -8,
    android: -8,
    ios: -8,
});
const dir = { dir: 'ltr' };
export const DatePickerView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.datePicker,
    style(theme, mediaQuery, window, props) {
        var _a;
        return {
            actionSheet: {
                display: mediaQuery.sm({})
                    ? 'none'
                    : 'flex'
            },
            container: {
                minWidth: 200,
                width: '100%'
            },
            modal: {
                flex: 1
            },
            card: {
                position: 'absolute',
                borderColor: ((_a = theme.colors) === null || _a === void 0 ? void 0 : _a.secondary_color) || 'rgb(17, 85, 204)'
            },
            body: {
                paddingTop: 30,
                paddingBottom: 24,
                paddingHorizontal: 30
            },
            input: {
                height: 50,
                borderRadius: 8,
                backgroundColor: '#fff',
                borderWidth: 1,
                borderStyle: 'solid',
                padding: 15,
                marginVertical: 1,
                marginHorizontal: 3
            },
            placeholder: Object.assign({ fontSize: 16, lineHeight: 18, color: 'rgb(115, 135, 159)' }, props.disabled && {
                opacity: 0.4
            }),
            text: Object.assign({ fontSize: 16, lineHeight: 18, color: '#394E66' }, props.disabled && {
                opacity: 0.4
            }),
            label: Object.assign({ zIndex: 1, lineHeight: 18, fontSize: 16, color: 'rgba(115, 135, 159, 1)', paddingHorizontal: 2, backgroundColor: '#fff', position: 'absolute' }, props.disabled && {
                color: 'rgba(115, 135, 159, 0.4)',
            })
        };
    }
})(({ style, value, disabled, label, before, placeholder, onChange, containerStyle, minDate, maxDate, disabledDates }) => {
    var _a, _b, _c, _d, _e;
    const ref = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [cardTop, setCardTop] = useState(0);
    const [cardLeft, setCardLeft] = useState(0);
    const [cardWidth, setCardWidth] = useState(0);
    const [shownDate, setShownDate] = useState(Date.create());
    const [currentDate, setCurrentDate] = useState(value);
    const [colorAnimation, setColorAnimation] = useAnimation({ initialValue: 0, duration: 200, nativeDriver: false });
    const [labelLeft, setLabelLeft] = useState(15);
    const [scale, setScale] = useAnimation({ initialValue: (currentDate || placeholder) && label ? 0.9 : 1, duration: 100, nativeDriver: false });
    const [top, setTop] = useAnimation({ initialValue: (currentDate || placeholder) ? TopPosition : CenterPosition, duration: 100, nativeDriver: false });
    const handleChange = useCallback((value) => {
        setCurrentDate(value);
        onChange === null || onChange === void 0 ? void 0 : onChange(value);
        if (value) {
            setTop(TopPosition);
            setScale(0.9);
        }
        setIsOpen(false);
        setColorAnimation(0);
    }, [onChange]);
    const handleHover = useCallback(() => {
        if (disabled) {
            return;
        }
        setColorAnimation(1);
    }, [disabled]);
    const handleBlur = useCallback(() => !isOpen && setColorAnimation(0), [isOpen]);
    const handleLayout = useCallback((e) => {
        setCardWidth(e.nativeEvent.layout.width || 0);
        if (Platform.OS === 'web') {
            return;
        }
        let left = (e.nativeEvent.layout.x || 0) - 10;
        if (left < 0) {
            left = 0;
        }
        setCardTop((e.nativeEvent.layout.y || 0) + 50);
        setCardLeft(left);
    }, []);
    const handleOpen = useCallback(() => {
        var _a;
        if (disabled) {
            return;
        }
        if (Platform.OS === 'web') {
            const rect = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            setCardLeft(rect.left);
            setCardTop(rect.top + rect.height + 10);
            setCardWidth(rect.width);
        }
        setIsOpen(true);
        setColorAnimation(1);
    }, [ref, disabled]);
    const handleClose = useCallback(() => {
        setIsOpen(false);
        setColorAnimation(0);
    }, []);
    const handleBeforeLayout = useCallback((e) => {
        setLabelLeft(15 + e.nativeEvent.layout.width);
    }, []);
    const handlePrevMonth = useCallback(() => {
        shownDate.removeMonths(1);
        setShownDate(shownDate.clone());
    }, [shownDate]);
    const handleNextMonth = useCallback(() => {
        shownDate.addMonths(1);
        setShownDate(shownDate.clone());
    }, [shownDate]);
    const borderColor = colorAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: ['rgb(191, 204, 218)', ((_a = style === null || style === void 0 ? void 0 : style.card) === null || _a === void 0 ? void 0 : _a.borderColor) || 'rgb(17, 85, 204)']
    });
    const labelStyles = [
        style === null || style === void 0 ? void 0 : style.label,
        {
            left: labelLeft,
            color: colorAnimation.interpolate({
                inputRange: [0, 1],
                outputRange: [((_b = style === null || style === void 0 ? void 0 : style.label) === null || _b === void 0 ? void 0 : _b.color) || 'rgb(115, 135, 159)', ((_c = style === null || style === void 0 ? void 0 : style.card) === null || _c === void 0 ? void 0 : _c.borderColor) || 'rgb(17, 85, 204)']
            }),
            transform: [
                { translateY: top },
                { scale }
            ]
        }
    ];
    return (_jsxs(View, Object.assign({ ref: ref, style: [style === null || style === void 0 ? void 0 : style.container, containerStyle], onLayout: handleLayout }, dir, { children: [!!label && (_jsx(AnimatedTextView, Object.assign({ textStyle: labelStyles }, Object.assign({ onMouseEnter: handleHover, onMouseLeave: handleBlur }, dir), { children: label }))), _jsxs(AnimatedPressable, Object.assign({ style: [style === null || style === void 0 ? void 0 : style.input, { borderColor }], onPress: handleOpen }, Object.assign({ onMouseEnter: handleHover, onMouseLeave: handleBlur }, dir), { children: [!!before && (_jsx(View, { style: style === null || style === void 0 ? void 0 : style.before, onLayout: handleBeforeLayout, children: before({ isSelected: isOpen }) })), !currentDate && !!placeholder && (_jsx(AnimatedTextView, { textStyle: style === null || style === void 0 ? void 0 : style.placeholder, children: placeholder })), !!currentDate && (_jsx(AnimatedTextView, { textStyle: style === null || style === void 0 ? void 0 : style.text, children: currentDate.getFormatedDate() }))] })), _jsx(Modal, Object.assign({ visible: isOpen, transparent: true, animationType: ((_d = style === null || style === void 0 ? void 0 : style.actionSheet) === null || _d === void 0 ? void 0 : _d.display) === 'none' ? 'fade' : 'slide', onRequestClose: handleClose, onDismiss: handleClose }, dir, { children: _jsx(Pressable, Object.assign({ onPress: handleClose, style: style === null || style === void 0 ? void 0 : style.modal }, dir, { children: _jsx(View, Object.assign({ style: ((_e = style === null || style === void 0 ? void 0 : style.actionSheet) === null || _e === void 0 ? void 0 : _e.display) === 'none'
                            ? Object.assign(Object.assign({}, style === null || style === void 0 ? void 0 : style.card), { top: cardTop, left: cardLeft, minWidth: cardWidth, alignItems: 'center' }) : Object.assign(Object.assign({}, style === null || style === void 0 ? void 0 : style.card), { bottom: 0, left: 0, right: 0, borderRadius: 0, elevation: 4, shadowOffset: { width: 1, height: 0, }, shadowRadius: 10 }) }, dir, { children: _jsxs(CardView, { bodyStyles: style === null || style === void 0 ? void 0 : style.body, children: [_jsx(DatePickerSelectorView, { title: shownDate.format('MMMM YYYY'), date: shownDate, minDate: minDate, maxDate: maxDate, onPrevMonth: handlePrevMonth, onNextMonth: handleNextMonth }), _jsx(DatePickerCalendarView, { minDate: minDate, maxDate: maxDate, disabledDates: disabledDates, selectedDate: currentDate, shownDate: shownDate, onChange: handleChange })] }) })) })) }))] })));
});
